<script lang="ts">
export default {
  name: 'KkTermSelect',
}
</script>

<script lang="ts" setup>
import { computed, ref, watch, useCssModule } from 'vue'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { KkButton, KkRadioGroup } from '../../base'
import MonthPicker from './MonthPicker.vue'
import { usePopUp } from '../../../composables/usePopUp'
import type { Moment } from 'moment'
import type { TermSelectValue, ComparisonOption } from './types'

export interface Props {
  modelValue: TermSelectValue
  type?: 'calendar' | 'month'
  comparison?: boolean
  comparisonOptions?: ComparisonOption[]
  min?: Moment
  max?: Moment
  disabled?: boolean
}

export interface Emits {
  (key: 'update:modelValue', value: Props['modelValue']): void
  (key: 'update:comparison', value: Props['comparison']): void
}

const props = withDefaults(defineProps<Props>(), {
  type: 'calendar',
  comparison: false,
  comparisonOptions: () => [],
  min: () => moment().subtract(2, 'year'),
  max: () => moment(),
})

const emits = defineEmits<Emits>()
const styles = useCssModule()
const { t, locale } = useI18n()
const comparisonType = ref('none')
const tempStartValue = ref<Moment | null>(moment())
const tempEndValue = ref<Moment | null>(moment())
const lastStartValue = ref<Moment | null>(null)
const lastEndValue = ref<Moment | null>(null)

const endInputRef = ref<HTMLInputElement>()

const componentId = computed(() => {
  const number = Math.floor(Math.random() * 10000)
  return `term-select-${number}`
})

const { isVisible: isVisibleModal } = usePopUp({
  buttonSelector: `#${componentId.value} .${styles.button}`,
  popUpSelector: `#${componentId.value} .${styles.modal}`,
})

const modelValues = computed({
  get: () => props.modelValue,
  set: (values) => emits('update:modelValue', values),
})

const modelStartValue = computed({
  get: () => props.modelValue.start,
  set: (value) => {
    emits('update:modelValue', Object.assign({}, props.modelValue, { start: value }))
  },
})

const modelEndValue = computed({
  get: () => props.modelValue.end,
  set: (value) => {
    emits('update:modelValue', Object.assign({}, props.modelValue, { end: value }))
  },
})

const isCompared = computed(() => props.comparison && modelValues.value.start && modelValues.value.end)

const comparisonOptions = computed(() => {
  if (!props.comparison) return []
  const options = [
    { key: 'none', name: t('labels.termSelect.noComparison'), value: null, disabled: false },
    ...props.comparisonOptions,
    { key: 'custom', name: t('labels.termSelect.custom'), value: null, disabled: false },
  ]
  return options
})

// MEMO: 左カラムに表示するラジオボタンの項目
const comparisonRadioOptions = computed(() => {
  if (!comparisonOptions.value) return []
  const options = comparisonOptions.value.map((option) => ({
    id: option.key,
    value: option.key,
    text: option.name,
    disabled: option.disabled,
  }))
  return options
})

const optimizeValue = (value: Moment, which: 'start' | 'end') => {
  // minより前の日付の場合、minとする
  if (props.min && value.isBefore(moment(props.min).subtract(1, 'month'))) return props.min

  // maxより後の日付の場合、maxとする
  if (props.max && value.isAfter(props.max)) return props.max

  // startの日付がendを超えていた場合、endの1ヶ月前とする
  if (which === 'start' && modelValues.value.end && value.isAfter(modelValues.value.end)) return moment(modelValues.value.end).subtract(1, 'month')

  // endの日付がstartより前になっていた場合、startの1ヶ月後とする
  if (which === 'end' && modelValues.value.start && value.isBefore(modelValues.value.start)) return moment(modelValues.value.start).add(1, 'month')

  return value
}

const handleInputStart = (event: Event) => {
  if (!event.target || !(event.target instanceof HTMLInputElement)) return

  lastStartValue.value = modelStartValue.value

  // MEMO: 年月選択の場合
  if (props.type === 'month') {
    if (!/^\d{4}\/\d{2}$/.test(event.target.value)) return
    const date = new Date(`${event.target.value}-01`)
    const newValue = moment(date.toISOString())

    modelStartValue.value = optimizeValue(newValue, 'start')
    if (endInputRef.value) endInputRef.value.focus()
  } else {
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(event.target.value)) return
    const newValue = moment(event.target.value)

    modelStartValue.value = optimizeValue(newValue, 'start')
    if (endInputRef.value) endInputRef.value.focus()
  }
}

const handleInputEnd = (event: Event) => {
  if (!event.target || !(event.target instanceof HTMLInputElement)) return

  lastEndValue.value = modelEndValue.value

  // MEMO: 年月選択の場合
  if (props.type === 'month') {
    if (!/^\d{4}\/\d{2}$/.test(event.target.value)) return
    const date = new Date(`${event.target.value}-01`)
    const newValue = moment(date.toISOString())

    modelEndValue.value = optimizeValue(newValue, 'end')
  } else {
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(event.target.value)) return
    const newValue = moment(event.target.value)

    modelEndValue.value = optimizeValue(newValue, 'end')
  }
}

const handleBlurStart = (event: Event) => {
  if (!event.target || !(event.target instanceof HTMLInputElement)) return
  if (props.type === 'month') {
    if (!/^\d{4}\/\d{2}$/.test(event.target.value)) {
      modelStartValue.value = lastStartValue.value
    }
  } else {
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(event.target.value)) {
      modelStartValue.value = lastStartValue.value
    }
  }
}

const handleBlurEnd = (event: Event) => {
  if (!event.target || !(event.target instanceof HTMLInputElement)) return
  if (props.type === 'month') {
    if (!/^\d{4}\/\d{2}$/.test(event.target.value)) {
      modelEndValue.value = lastEndValue.value
    }
  } else {
    if (!/^\d{4}\/\d{2}\/\d{2}$/.test(event.target.value)) {
      modelEndValue.value = lastEndValue.value
    }
  }
}

const handleClick = () => {
  if (props.disabled) return
  isVisibleModal.value = true
}

const handleKeydown = (event: KeyboardEvent) => {
  if (props.disabled) return
  if (event.key === 'Enter' || event.key === ' ') {
    isVisibleModal.value = true
  }
}

const handleCancel = () => {
  isVisibleModal.value = false
  tempStartValue.value = modelStartValue.value
  tempEndValue.value = modelEndValue.value
}

const handleConfirm = () => {
  modelValues.value = {
    start: tempStartValue.value,
    end: tempEndValue.value,
  }
  isVisibleModal.value = false
}

const handleChangePicker = () => {
  comparisonType.value = 'custom'
}

const handleKeydownStart = (event: KeyboardEvent) => {
  if (event.key === 'Tab' && event.shiftKey) {
    isVisibleModal.value = false
  } else if (event.key === 'Enter' && !event.isComposing && endInputRef.value) {
    endInputRef.value.focus()
  }
}

const handleKeydownEnd = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    isVisibleModal.value = false
  } else if (event.key === 'Enter') {
    event.stopPropagation()
    isVisibleModal.value = false
  }
}

const updateComparisonType = (key: string) => {
  if (key === 'none') {
    tempStartValue.value = null
    tempEndValue.value = null
    return
  }

  const selectedOption = props.comparisonOptions.find((option) => option.key === key)
  if (!selectedOption) return

  tempStartValue.value = selectedOption.value.start
  tempEndValue.value = selectedOption.value.end
  comparisonType.value = key
}

watch(isVisibleModal, (value) => {
  if (value) {
    tempStartValue.value = modelStartValue.value
    tempEndValue.value = modelEndValue.value

    if (tempStartValue.value && tempEndValue.value) {
      for (const option of props.comparisonOptions) {
        if (!tempStartValue.value.isSame(option.value.start, 'month') || !tempEndValue.value.isSame(option.value.end, 'month')) continue
        comparisonType.value = option.key
        return
      }

      comparisonType.value = 'custom'
    } else {
      comparisonType.value = 'none'
    }
  }
})

watch(modelValues, (value) => {
  tempStartValue.value = value.start
  tempEndValue.value = value.end
})

tempStartValue.value = modelStartValue.value
tempEndValue.value = modelEndValue.value
</script>

<template>
  <div :id="componentId" :class="$style.select">
    <div :aria-disabled="props.disabled" tabindex="0" :class="$style.button" @click="handleClick" @keydown="handleKeydown">
      <template v-if="props.comparison && !props.modelValue.start && !props.modelValue.end">
        <span :class="$style.field">{{ $t('labels.termSelect.noTermComparison') }}</span>
      </template>
      <template v-else>
        <span v-if="isCompared && locale === 'en'" :class="[$style.comparison, $style.en]">{{ $t('labels.termSelect.compareWith') }}</span>
        <input
          :value="props.modelValue.start?.format('YYYY/MM')"
          :disabled="props.disabled"
          type="text"
          :class="$style.field"
          @input="handleInputStart"
          @blur="handleBlurStart"
          @keydown="handleKeydownStart"
        >
        <span>-</span>
        <input
          ref="endInputRef"
          :value="props.modelValue.end?.format('YYYY/MM')"
          :disabled="props.disabled"
          type="text"
          :class="$style.field"
          @input="handleInputEnd"
          @blur="handleBlurEnd"
          @keydown="handleKeydownEnd"
        >
        <span v-if="isCompared && locale === 'ja'" :class="$style.comparison">{{ $t('labels.termSelect.compareWith') }}</span>
      </template>
    </div>

    <transition name="modal">
      <div v-if="isVisibleModal" tabindex="-1" :class="$style.modal">
        <div :class="$style.container">
          <div v-if="props.comparison" :class="$style.options" class="options">
            <KkRadioGroup :model-value="comparisonType" :items="comparisonRadioOptions" name="comparison" @update:model-value="updateComparisonType" />
          </div>
          <div :class="$style.picker">
            <template v-if="props.type === 'month'">
              <MonthPicker v-model:start="tempStartValue" v-model:end="tempEndValue" :min="props.min" :max="props.max" @change="handleChangePicker" />
            </template>
          </div>
        </div>
        <div :class="$style.footer">
          <KkButton :text="$t('labels.cancel')" color="secondary" @click="handleCancel" />
          <KkButton :text="$t('labels.confirm')" color="primary" @click="handleConfirm" />
        </div>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" module>
@import '../../../../../stylesheets/biz_spa/foundation/var';
@import '../../../../../stylesheets/biz_spa/foundation/mixin';

.select {
  position: relative;
  width: fit-content;
}

.button {
  position: relative;
  border-radius: 4px;
  border: 1px solid $color-base-c;
  padding-right: 26px;
  width: fit-content;
  background: $color-base-a;
  font-size: 14px;
  line-height: 1.5;
  color: $color-font-base;
  cursor: pointer;

  &::after {
    position: absolute;
    right: 16px;
    top: 50%;
    content: '';
    margin-top: -2px;
    border-width: 4px 5px;
    border-style: solid;
    border-color: currentColor transparent transparent;
  }

  &[aria-disabled='true'] {
    color: #E3E3E3;
    cursor: not-allowed;
  }
}

.field {
  display: inline-block;
  padding: 7px 15px 5px;
  width: 6.5em;
  white-space: nowrap;

  &:first-child {
    padding-right: 8px;
  }

  &:last-child {
    padding-left: 8px;
  }

  &:only-child {
    padding: 7px 15px 5px;
    width: auto;
  }
}

.comparison {
  display: inline-block;
  padding-right: 8px;

  &.en {
    padding: 0 0 0 15px;
  }
}

.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 10;
  transform: translate(-50%, -50%);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  min-width: calc(100vw - 24px);
  max-width: 532px;
  background: $color-base-a;

  @include mq() {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    left: auto;
    z-index: 2000;
    min-width: max-content;
    transform: none;
  }
}

.container {
  display: flex;
}

.options {
  border-right: 1px solid $color-base-c;
  padding: 20px;

  @include mq() {
    padding: 32px;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0 16px;
  border-top: 1px solid $color-base-c;
  padding: 20px 32px;
}
</style>

<style lang="scss" scoped>
@import '../../../../../stylesheets/biz_spa/foundation/var';

.options {
  :deep(.radio) {
    gap: 24px;
    flex-direction: column;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
